import React, { createContext, useContext, useEffect, useState } from 'react'
import DetailsBox, { DetailRow } from '../../../components/Cards/DetailsBox'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import {
  Autocomplete,
  Button,
  ButtonGroup,
  Grid,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import Edit from '@mui/icons-material/Edit'
import Save from '@mui/icons-material/Save'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import Cancel from '@mui/icons-material/Cancel'
import BigModal from '../../../components/Modals/BigModal'
import Link from '@mui/material/Link'
import validator from 'validator'
import AddNewClient from '../AddNewClient'
import MenuIcon from '@mui/icons-material/Menu'
import SmallModal from '../../../components/Modals/SmallModal'
import { useNavigate } from 'react-router-dom'

const ClientInfoContext = createContext()

const ClientInformation = ({ client }) => {
  const [formValues, setFormValues] = useState({})
  const [editMode, setEditMode] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [openConfirmArchive, setOpenConfirmArchive] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()

  const handleArchiveClient = () => {
    API.put(`/pts/organisations/${client._id}`, {
      archived: true,
    }).then((res) => {
      if (res.status === 200) {
        navigate('/pts/organisations')
        setToast({ message: 'Organisation Archived' })
      }
    })
  }

  const handleUnarchive = () => {
    API.put(`/pts/organisations/${client._id}`, {
      archived: false,
    }).then((res) => {
      if (res.status === 200) {
        setMenuAnchor(null)
        setToast({ message: 'Organisation Reinstated' })
      }
    })
  }

  const loadDefaults = () =>
    setFormValues({
      _id: client?._id ?? null,
      orgName: client?.orgName ?? '',
      orgType: client?.orgType ?? '',
      orgDescription: client?.orgDescription ?? '',
      orgWebsite: client?.orgWebsite ?? '',
      orgTel: client?.orgTel ?? '',
      orgEmail: client?.orgEmail ?? '',
      financeContact: client?.financeContact ?? { name: '', email: '', phone: '' },
      orgAddressLineOne: client?.orgAddressLineOne ?? '',
      orgAddressLineTwo: client?.orgAddressLineTwo ?? '',
      orgAddressTown: client?.orgAddressTown ?? '',
      orgAddressCity: client?.orgAddressCity ?? '',
      orgAddressPostCode: client?.orgAddressPostCode ?? '',
      baseLocation: client?.baseLocation ?? null,
    })

  useEffect(() => loadDefaults(), [client])

  const handleSave = () => {
    API.put(`/event-planning/organisations/${client._id}`, formValues).then(
      (res) => {
        if (res.status === 200) {
          setEditMode(false)
          setToast({ message: 'Organisation Information Updated' })
        } else if (res.data?.errorCode === 'noMatchingGeocode') {
          setToast({
            message:
              'We could not find that address on the map. Please check the address fields and try again.',
            severity: 'error',
          })
        } else {
          setToast({
            message:
              res.data?.error || 'Unable to save organisation information.',
            severity: 'error',
          })
        }
      },
    )
  }

  return (
    <ClientInfoContext.Provider value={{ formValues, setFormValues, editMode }}>
      <DetailsBox
        title="Client Information"
        buttonRight={
          <Stack direction="row" gap={1}>
            <MenuIcon
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ cursor: 'pointer' }}
              color="primary"
              fontSize="small"
            />
            <Edit
              onClick={() => setEditMode((prev) => !prev)}
              sx={{ cursor: 'pointer' }}
              color="primary"
              fontSize="small"
            />
          </Stack>
        }
        rows={[
          { label: 'Name', value: formValues?.orgName ?? '' },
          { label: 'Type', value: formValues?.orgType ?? '' },
          {
            label: 'Base Location',
            value: formValues?.baseLocation?.name || '',
          },
          {
            label: 'Description',
            value: formValues?.orgDescription || '',
            fullWidth: true,
          },
          {
            label: 'Address',
            value: [
              formValues.orgAddressLineOne,
              formValues.orgAddressLineTwo,
              formValues.orgAddressTown,
              formValues.orgAddressCity,
              formValues.orgAddressPostCode,
            ]
              .filter(Boolean)
              .join(', '),
            fullWidth: true,
          },
          {
            label: 'Phone',
            value: formValues?.orgTel && (
              <Link href={`tel:${formValues.orgTel}`}>{formValues.orgTel}</Link>
            ),
            fullWidth: true,
          },
          {
            label: 'Website',
            value: validator.isURL(formValues.orgWebsite ?? '') && (
              <Link
                href={
                  formValues.orgWebsite?.startsWith('http')
                    ? formValues.orgWebsite
                    : `https://${formValues.orgWebsite}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {formValues.orgWebsite}
              </Link>
            ),
          },
          {
            label: 'Email',
            value: validator.isEmail(formValues?.orgEmail ?? '') && (
              <Link href={`mailto:${formValues.orgEmail}`} target="_blank">
                {formValues?.orgEmail ?? ''}
              </Link>
            ),
          },
          {
            label: 'Finance Contact',
            value: validator.isEmail(formValues?.financeContact?.email ?? '') ? (
              <Link
                href={`mailto:${formValues.financeContact.email}`}
                target="_blank"
              >
                {formValues.financeContact?.name
                  ? `${formValues.financeContact.name} (${formValues.financeContact.email})`
                  : formValues.financeContact.email}
              </Link>
            ) : (
              'Same as organisation email'
            ),
          },
        ]}
      />
      <Menu
        open={!!menuAnchor}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        {client.archived ? (
          <MenuItem onClick={() => handleUnarchive()}>
            Unarchive Organisation
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setOpenConfirmArchive(true)
              setMenuAnchor(null)
            }}
          >
            Archive Organisation
          </MenuItem>
        )}
      </Menu>
      <SmallModal
        open={openConfirmArchive}
        onClose={() => setOpenConfirmArchive(false)}
      >
        <Stack gap={1}>
          <Typography textAlign="center">
            Are you sure you want to archive this client?
          </Typography>
          <Button
            fullWidth
            color="anGreen"
            variant="contained"
            size="small"
            onClick={() => handleArchiveClient()}
          >
            Yes - Archive
          </Button>
          <Button
            fullWidth
            color="anRed"
            variant="contained"
            size="small"
            onClick={() => setOpenConfirmArchive(false)}
          >
            Cancel
          </Button>
        </Stack>
      </SmallModal>
      <BigModal
        open={editMode}
        onClose={() => {
          setEditMode(false)
        }}
      >
        <AddNewClient
          editMode={true}
          existingClient={formValues}
          customCallbackAction={(clientInfo) => {
            setFormValues(clientInfo)
            setEditMode(false)
          }}
        />
      </BigModal>
    </ClientInfoContext.Provider>
  )
}

export default ClientInformation
